<template>
  <div class="map" ref="mapEl" />
</template>

<script>
import { mapState } from 'vuex'
import { mapLoader } from '@/utils/googleMap'
import { cookies } from '@/utils/cookies'
import { driverStatus } from '@/constants/driver'
import destination from '@/assets/images/map/destination.png'
import flag from '@/assets/images/map/flag.png'
import kmlTool from '@/assets/js/tool/kmlTool.js'

export default {
  data() {
    return {
      map: null,
      markers: [],
      polyLines: [],
      kmlParser: null,
    }
  },
  computed: {
    ...mapState('hailStateV2', {
      vx_driverInfo: 'driverInfo',
    }),
  },
  methods: {
    // 新增點或線
    async add(type, options) {
      switch (type) {
        case 'marker':
          await google.maps.importLibrary('marker')
          const marker = await new google.maps.marker.AdvancedMarkerElement(
            options,
          )
          this.markers.push(marker)
          break
        case 'polyLine':
          const polyLine = new google.maps.Polyline(options)
          this.polyLines.push(polyLine)
          break
        default:
          break
      }
    },
    // 將點與線移除於地圖上
    delete() {
      this.setOnMap(null)
      this.markers = []
      this.polyLines = []
    },
    // 將點與線放於地圖上
    setOnMap(map) {
      this.markers.map((marker) => marker.setMap(map))
      this.polyLines.map((polyLine) => polyLine.setMap(map))
    },
    plotMap() {
      // 移除地圖上舊有的點與線
      this.delete()
      const {
        driverPositionGps,
        driverGrabOrderGps,
        passengerPickUpLocationGps,
        driverStatusNumber: status,
        driverCallNumber,
      } = this.vx_driverInfo
      if (
        driverPositionGps &&
        driverPositionGps.latitude &&
        driverPositionGps.longitude
      ) {
        const { latitude: lat, longitude: lng } = driverPositionGps
        // 標記車子位置
        const figEl = document.createElement('figure')
        const imgEl = document.createElement('img')
        imgEl.src = status ? driverStatus[status].img : ''
        const captionEl = document.createElement('figcaption')
        captionEl.textContent = driverCallNumber
        captionEl.style.color = 'black'
        captionEl.style.fontWeight = '700'
        captionEl.style.fontSize = '1.6rem'
        figEl.appendChild(imgEl)
        figEl.appendChild(captionEl)
        this.add('marker', {
          position: { lat, lng },
          map: this.map,
          title: driverCallNumber,
          content: figEl,
        })
        // 將地圖的中心點設置在車子位置
        this.map.setCenter({
          lat,
          lng,
        })
      }
      if (
        driverGrabOrderGps &&
        driverGrabOrderGps.latitude &&
        driverGrabOrderGps.longitude
      ) {
        const { latitude: lat, longitude: lng } = driverGrabOrderGps
        // 標記司機接單位置
        const imgEl = document.createElement('img')
        imgEl.src = flag
        this.add('marker', {
          position: {
            lat,
            lng,
          },
          map: this.map,
          content: imgEl,
        })
        // 畫出線條
        if (
          driverPositionGps &&
          driverPositionGps.latitude &&
          driverPositionGps.longitude
        ) {
          this.add('polyLine', {
            path: [
              {
                lat: driverPositionGps.latitude,
                lng: driverPositionGps.longitude,
              },
              {
                lat,
                lng,
              },
            ],
            geodesic: true,
            strokeColor: '#005ce6',
            strokeOpacity: 1.0,
            strokeWeight: 2,
          })
        }
      }
      if (
        passengerPickUpLocationGps &&
        passengerPickUpLocationGps.latitude &&
        passengerPickUpLocationGps.longitude
      ) {
        const { latitude: lat, longitude: lng } = passengerPickUpLocationGps
        // 標記乘客上車位置
        const imgEl = document.createElement('img')
        imgEl.src = destination
        this.add('marker', {
          position: { lat, lng },
          map: this.map,
          content: imgEl,
        })
        // 若沒有汽車位置將地圖的中心點設置在乘客位置
        if (!driverPositionGps.latitude || !driverPositionGps.longitude) {
          this.map.setCenter({
            lat,
            lng,
          })
        }
        // 畫出線條
        if (
          driverPositionGps &&
          driverPositionGps.latitude &&
          driverPositionGps.longitude
        ) {
          this.add('polyLine', {
            path: [
              {
                lat: driverPositionGps.latitude,
                lng: driverPositionGps.longitude,
              },
              {
                lat,
                lng,
              },
            ],
            geodesic: true,
            strokeColor: '#FF0000',
            strokeOpacity: 1.0,
            strokeWeight: 2,
          })
        }
      }
      // 將點與線放到地圖上
      this.setOnMap(this.map)
    },
    setKmlOnMap(map) {
      this.kmlParser = new geoXML3.parser({
        map: map,
        singleInfoWindow: true,
        zoom: false,
      })
      kmlTool.getDispatchCode()
      kmlTool.getSchualedCode()
      kmlTool.showDispatchKml(this.kmlParser, map)
      kmlTool.showSchualedKml(this.kmlParser, map)
    },
    // 將地圖的中心點設置在cookie中的點位座標
    setMapCenter() {
      if (this.map) {
        const lat = parseFloat(cookies.get('GladAddressGps').coordinates[1])
        const lng = parseFloat(cookies.get('GladAddressGps').coordinates[0])
        this.map.setCenter({ lat, lng })
      }
    },
  },
  mounted() {
    const lat = parseFloat(cookies.get('GladAddressGps').coordinates[1])
    const lng = parseFloat(cookies.get('GladAddressGps').coordinates[0])
    const loader = mapLoader()
    loader.load().then(async () => {
      const { Map } = await google.maps.importLibrary('maps')
      this.map = new Map(this.$refs.mapEl, {
        mapId: 'mapId',
        zoom: 14,
        center: { lat, lng },
        streetViewControl: false,
        gestureHandling: 'greedy',
      })
      this.setKmlOnMap(this.map)
    })
  },
  created() {
    this.unwatchDriverInfo = this.$store.watch(
      (state) => state.hailStateV2.driverInfo,
      this.plotMap,
      {
        deep: true,
      },
    )
    this.unwatchRefreshClick = this.$store.watch(
      (state) => state.hailStateV2.refreshClick,
      this.setMapCenter,
    )
  },
  beforeDestroy() {
    this.unwatchDriverInfo()
    this.unwatchRefreshClick()
  },
}
</script>

<style scoped>
.map {
  margin: 0 left;
  background: gray;
}
</style>



// WEBPACK FOOTER //
// src/pages/hailState/components-v2/Map.vue